import React, { useState } from "react";
import Loader from "../../../components/common/Loader";
import authService from "../../../Services/authService";
import { checkPasswordRules } from "../../../utils/passwordValidator";
import PasswordStrengthChecklist from "../../../components/common/PasswordStrengthChecklist";

const ChangePassword = () => {
    const currentUser = authService.getUser() || {};
    const userContext = {
        name: currentUser.name || `${currentUser.f_name || ''} ${currentUser.l_name || ''}`.trim(),
        email: currentUser.email,
        mobile: currentUser.phone || currentUser.mobile
    };

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");

    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [generalError, setGeneralError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const rules = checkPasswordRules(newPassword, userContext);
    const isAllRulesMet = rules.isValid;

    const validateForm = () => {
        const errors = {};

        if (!currentPassword) {
            errors.currentPassword = "Current password is required";
        }

        if (!newPassword) {
            errors.newPassword = "New password is required";
        } else if (!isAllRulesMet) {
            errors.newPassword = "New password does not meet security requirements";
        } else if (currentPassword && newPassword === currentPassword) {
            errors.newPassword = "New password must be different from current password";
        }

        if (!confirmNewPassword) {
            errors.confirmNewPassword = "Confirm password is required";
        } else if (newPassword !== confirmNewPassword) {
            errors.confirmNewPassword = "Passwords do not match";
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setGeneralError(null);
        setSuccessMessage(null);

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const res = await authService.changePassword(
                currentPassword,
                newPassword,
                confirmNewPassword
            );

            setSuccessMessage(res.msg || res.message || "Password changed successfully!");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmNewPassword("");
            setFieldErrors({});
        } catch (err) {
            const msg = err.message || "Failed to change password.";

            if (msg.includes("Invalid current password") || msg.includes("Incorrect current password")) {
                setFieldErrors({ currentPassword: "Incorrect current password" });
            } else if (
                msg.includes("Cannot use a recently used password") ||
                msg.includes("recently used password") ||
                msg.includes("cannot be the same") ||
                msg.includes("reuse")
            ) {
                setFieldErrors({ newPassword: msg });
            } else if (msg.includes("Passwords do not match") || msg.includes("match")) {
                setFieldErrors({ confirmNewPassword: "Passwords do not match" });
            } else {
                setGeneralError(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
        setFieldErrors({});
        setGeneralError(null);
        setSuccessMessage(null);
    };

    return (
        <>
            {loading && <Loader />}

            <div className="change-password-page-wrapper">
                <div className="settings-card">
                    {/* Header */}
                    <div className="settings-card-header">
                        <div className="icon-badge">
                            <i className="material-icons">lock_reset</i>
                        </div>
                        <div className="header-text">
                            <h4>Change Account Password</h4>
                            <p>Update your password regularly to maintain clinical data security.</p>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="settings-card-body">
                        {generalError && (
                            <div className="alert-banner alert-danger" role="alert">
                                <i className="material-icons">error_outline</i>
                                <span>{generalError}</span>
                            </div>
                        )}

                        {successMessage && (
                            <div className="alert-banner alert-success" role="alert">
                                <i className="material-icons">check_circle_outline</i>
                                <span>{successMessage}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} noValidate>
                            {/* Current Password */}
                            <div className="form-field">
                                <label htmlFor="settings-current-password">
                                    Current Password <span className="required-star">*</span>
                                </label>
                                <div className="password-input-group">
                                    <input
                                        id="settings-current-password"
                                        type={showCurrent ? "text" : "password"}
                                        name="currentPassword"
                                        autoComplete="current-password"
                                        placeholder="Enter your current password"
                                        value={currentPassword}
                                        onChange={(e) => {
                                            setCurrentPassword(e.target.value);
                                            if (fieldErrors.currentPassword) {
                                                setFieldErrors((prev) => ({ ...prev, currentPassword: "" }));
                                            }
                                        }}
                                        className={fieldErrors.currentPassword ? "has-error" : ""}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => setShowCurrent(!showCurrent)}
                                        aria-label={showCurrent ? "Hide current password" : "Show current password"}
                                    >
                                        <i className="material-icons">
                                            {showCurrent ? "visibility_off" : "visibility"}
                                        </i>
                                    </button>
                                </div>
                                {fieldErrors.currentPassword && (
                                    <span className="field-error-msg">{fieldErrors.currentPassword}</span>
                                )}
                            </div>

                            {/* New Password */}
                            <div className="form-field">
                                <label htmlFor="settings-new-password">
                                    New Password <span className="required-star">*</span>
                                </label>
                                <div className="password-input-group">
                                    <input
                                        id="settings-new-password"
                                        type={showNew ? "text" : "password"}
                                        name="newPassword"
                                        autoComplete="new-password"
                                        placeholder="Min. 8 chars, 1 uppercase, 1 digit, 1 symbol"
                                        value={newPassword}
                                        onChange={(e) => {
                                            setNewPassword(e.target.value);
                                            if (fieldErrors.newPassword) {
                                                setFieldErrors((prev) => ({ ...prev, newPassword: "" }));
                                            }
                                        }}
                                        className={fieldErrors.newPassword ? "has-error" : ""}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => setShowNew(!showNew)}
                                        aria-label={showNew ? "Hide new password" : "Show new password"}
                                    >
                                        <i className="material-icons">
                                            {showNew ? "visibility_off" : "visibility"}
                                        </i>
                                    </button>
                                </div>
                                {fieldErrors.newPassword && (
                                    <span className="field-error-msg">{fieldErrors.newPassword}</span>
                                )}

                                {/* Password Requirements Checklist */}
                                <div className="mt-2">
                                    <PasswordStrengthChecklist rules={rules} title="Password requirements:" />
                                </div>
                            </div>

                            {/* Confirm New Password */}
                            <div className="form-field">
                                <label htmlFor="settings-confirm-password">
                                    Confirm New Password <span className="required-star">*</span>
                                </label>
                                <div className="password-input-group">
                                    <input
                                        id="settings-confirm-password"
                                        type={showConfirm ? "text" : "password"}
                                        name="confirmNewPassword"
                                        autoComplete="new-password"
                                        placeholder="Repeat new password"
                                        value={confirmNewPassword}
                                        onChange={(e) => {
                                            setConfirmNewPassword(e.target.value);
                                            if (fieldErrors.confirmNewPassword) {
                                                setFieldErrors((prev) => ({ ...prev, confirmNewPassword: "" }));
                                            }
                                        }}
                                        className={fieldErrors.confirmNewPassword ? "has-error" : ""}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => setShowConfirm(!showConfirm)}
                                        aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"}
                                    >
                                        <i className="material-icons">
                                            {showConfirm ? "visibility_off" : "visibility"}
                                        </i>
                                    </button>
                                </div>
                                {fieldErrors.confirmNewPassword && (
                                    <span className="field-error-msg">{fieldErrors.confirmNewPassword}</span>
                                )}
                            </div>

                            {/* Form Actions */}
                            <div className="form-actions">
                                <button
                                    type="submit"
                                    className="btn-submit-password"
                                    disabled={loading}
                                >
                                    {loading && <span className="btn-spinner" />}
                                    {loading ? "Updating Password..." : "Update Password"}
                                </button>
                                <button
                                    type="button"
                                    className="btn-reset-form"
                                    onClick={handleReset}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ChangePassword;
